import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { Check, Copy, ExternalLink, ImagePlus, X, Eye, Save, AlertCircle, Palette } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { buildPortfolioUrl, slugify, slugExists, SLUG_RE } from '../services/portfolio';
import {
  PORTFOLIO_THEME_LIST,
  PORTFOLIO_THEMES,
  PORTFOLIO_FONTS,
  PORTFOLIO_LAYOUTS,
  PORTFOLIO_GROUPINGS,
  resolvePortfolioTheme,
} from '../data/portfolioThemes';
import type { PortfolioConfig, PortfolioFont, PortfolioGrouping, PortfolioLayout, PortfolioTheme } from '../types';
import { cn, fileToDataUrl, resizeImage } from '../utils/formatters';
import { input, label } from '../styles/components';

const ACCENT_PRESETS = [
  '#2563eb',
  '#4f46e5',
  '#0284c7',
  '#2f7d55',
  '#c2571f',
  '#f97316',
  '#a9842f',
  '#d4af37',
  '#7c3aed',
  '#111111',
];

export function PortfolioManagePage() {
  const { company, portfolio, projects, photos, updatePortfolio } = useCompany();
  const [form, setForm] = useState({
    enabled: false,
    title: '',
    subtitle: '',
    description: '',
    coverImage: '',
    contactPhone: '',
    contactEmail: '',
    contactWhatsapp: '',
    slug: '',
    featuredProjectIds: [] as string[],
    theme: 'clasico' as PortfolioTheme,
    accentColor: '',
    font: '' as PortfolioFont | '',
    layout: 'grid' as PortfolioLayout,
    grouping: 'none' as PortfolioGrouping,
  });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!portfolio) return;
    setForm({
      enabled: portfolio.enabled,
      title: portfolio.title || '',
      subtitle: portfolio.subtitle || '',
      description: portfolio.description || '',
      coverImage: portfolio.coverImage || '',
      contactPhone: portfolio.contactPhone || '',
      contactEmail: portfolio.contactEmail || '',
      contactWhatsapp: portfolio.contactWhatsapp || '',
      slug: portfolio.slug || '',
      featuredProjectIds: portfolio.featuredProjectIds || [],
      theme: portfolio.theme ?? 'clasico',
      accentColor: portfolio.accentColor ?? '',
      font: portfolio.font ?? '',
      layout: portfolio.layout ?? 'grid',
      grouping: portfolio.grouping ?? 'none',
    });
  }, [portfolio]);

  if (!company || !portfolio) {
    return <div className="p-8 text-center b2b-muted">Cargando portfolio…</div>;
  }

  const preset = PORTFOLIO_THEMES[form.theme] ?? PORTFOLIO_THEMES.clasico;
  const effectiveFont = form.font || preset.font;
  const effectiveAccent = form.accentColor || preset.accent;
  const previewConfig: PortfolioConfig = {
    ...portfolio,
    theme: form.theme,
    accentColor: form.accentColor || undefined,
    font: form.font || undefined,
    layout: form.layout,
    grouping: form.grouping,
  };

  const coverOptions = photos.filter(p => p.url).slice(0, 24);
  const publicUrl = buildPortfolioUrl(form.slug);
  const slugValid = SLUG_RE.test(form.slug);
  const slugTaken = slugValid && slugExists(form.slug, company.id);
  const slugOk = slugValid && !slugTaken;

  const toggleProject = (id: string) => {
    setForm(prev => ({
      ...prev,
      featuredProjectIds: prev.featuredProjectIds.includes(id)
        ? prev.featuredProjectIds.filter(x => x !== id)
        : [...prev.featuredProjectIds, id],
    }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.title.trim()) {
      setError('El título es obligatorio.');
      return;
    }
    if (!slugOk) {
      setError(slugTaken ? 'Ese enlace ya está en uso por otra empresa.' : 'El enlace solo admite minúsculas, números y guiones (3-40 caracteres).');
      return;
    }
    await updatePortfolio({
      enabled: form.enabled,
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      description: form.description.trim(),
      coverImage: form.coverImage || undefined,
      contactPhone: form.contactPhone || undefined,
      contactEmail: form.contactEmail || undefined,
      contactWhatsapp: form.contactWhatsapp || undefined,
      slug: form.slug,
      featuredProjectIds: form.featuredProjectIds,
      theme: form.theme,
      accentColor: form.accentColor || undefined,
      font: (form.font || undefined) as PortfolioFont | undefined,
      layout: form.layout,
      grouping: form.grouping,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('No se pudo copiar el enlace.');
    }
  };

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen (JPG, PNG o WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no debe superar 5MB.');
      return;
    }
    setError(null as unknown as never);
    const raw = await fileToDataUrl(file);
    const resized = await resizeImage(raw, 1600, 1100, 0.82);
    setForm(prev => ({ ...prev, coverImage: resized }));
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Portfolio</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Portfolio público</h1>
          <p className="b2b-muted">Comparte tus mejores obras con un enlace para tus clientes.</p>
        </div>
        <span className={cn('b2b-pill', form.enabled ? 'b2b-pill--ok' : '')}>
          {form.enabled ? 'Publicado' : 'Oculto'}
        </span>
      </div>

      <div className="b2b-panel p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="b2b-card__label">Enlace público</p>
            <p className="font-mono text-sm truncate">{publicUrl}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={copyLink} className="b2b-btn b2b-btn--ghost b2b-btn--sm">
              {saved ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar</>}
            </button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="b2b-btn b2b-btn--ghost b2b-btn--sm" aria-label="Abrir portfolio">
              <Eye className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {error && (
        <div className="b2b-pill b2b-pill--danger px-4 py-3" role="alert">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Datos generales</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium">Publicar portfolio</p>
              <p className="text-sm b2b-muted">Visible públicamente mediante el enlace.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, enabled: !form.enabled })}
              className={cn('b2b-btn b2b-btn--sm', form.enabled ? 'b2b-btn--primary' : 'b2b-btn--ghost')}
            >
              {form.enabled ? 'Publicado' : 'Oculto'}
            </button>
          </div>

          <div>
            <label className={label}>Título *</label>
            <input className={input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Reformas Hermes" />
          </div>

          <div>
            <label className={label}>Subtítulo</label>
            <input className={input} value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} placeholder="Reformas integrales en Madrid" />
          </div>

          <div>
            <label className={label}>Descripción</label>
            <textarea className={cn(input, 'min-h-[100px]')} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>

          <div>
            <label className={label}>Enlace (slug)</label>
            <div className="flex items-center gap-2">
              <span className="b2b-muted text-sm whitespace-nowrap">/portfolio/</span>
              <input
                className={cn(input, !slugOk && form.slug.length > 0 && 'border-[color:var(--b2b-danger)]')}
                value={form.slug}
                onChange={e => setForm({ ...form, slug: slugify(e.target.value) })}
              />
            </div>
            {form.slug && !slugOk && (
              <p className="mt-1 text-sm text-[color:var(--b2b-danger)]">
                {slugTaken ? 'Ese enlace ya está en uso.' : 'Introduce al menos 3 caracteres (a-z, 0-9, guiones).'}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="b2b-panel p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-[color:var(--b2b-primary)]" />
          <h2 className="text-lg font-semibold">Diseño</h2>
        </div>

        <div>
          <label className={label}>Estilo</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
            {PORTFOLIO_THEME_LIST.map(theme => (
              <button
                key={theme.id}
                type="button"
                onClick={() => setForm(f => ({ ...f, theme: theme.id }))}
                className={cn(
                  'rounded-xl border p-3 text-left transition',
                  form.theme === theme.id
                    ? 'border-[color:var(--b2b-primary)] ring-2 ring-[color:var(--b2b-primary)]/25'
                    : 'border-[var(--b2b-border)] hover:border-[color:var(--b2b-primary)]'
                )}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="w-7 h-7 rounded-full border shrink-0"
                    style={{ background: theme.accent, borderColor: theme.border }}
                  />
                  <span
                    className="w-5 h-5 rounded border shrink-0"
                    style={{ background: theme.bg, borderColor: theme.border }}
                  />
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ background: theme.surface, borderColor: theme.border, borderWidth: 1 }}
                  />
                </span>
                <span className="block mt-2 text-sm font-medium">{theme.name}</span>
                <span className="block text-xs b2b-muted">{theme.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={label}>Color de marca</label>
          <div className="flex flex-wrap items-center gap-2">
            {ACCENT_PRESETS.map(color => (
              <button
                key={color}
                type="button"
                aria-label={`Color ${color}`}
                onClick={() => setForm(f => ({ ...f, accentColor: color }))}
                className={cn(
                  'w-8 h-8 rounded-full border-2 transition',
                  form.accentColor.toLowerCase() === color
                    ? 'border-[color:var(--b2b-text)] scale-110'
                    : 'border-transparent hover:scale-110'
                )}
                style={{ background: color }}
              />
            ))}
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(form.accentColor) ? form.accentColor : preset.accent}
              onChange={e => setForm(f => ({ ...f, accentColor: e.target.value }))}
              className="w-9 h-8 rounded border border-[var(--b2b-border)] bg-transparent cursor-pointer"
              aria-label="Color personalizado"
            />
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, accentColor: '' }))}
              className="b2b-btn b2b-btn--ghost b2b-btn--sm"
            >
              Usar el del tema
            </button>
          </div>
          <p className="mt-1.5 text-xs b2b-muted">
            Color actual: <span className="font-mono">{effectiveAccent}</span>
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6">
          <div>
            <label className={label}>Tipografía</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(PORTFOLIO_FONTS) as PortfolioFont[]).map(fontId => (
                <Choice
                  key={fontId}
                  label={PORTFOLIO_FONTS[fontId].label}
                  fontFamily={PORTFOLIO_FONTS[fontId].heading}
                  active={effectiveFont === fontId}
                  onClick={() => setForm(f => ({ ...f, font: fontId }))}
                />
              ))}
            </div>
          </div>
          <div>
            <label className={label}>Formato</label>
            <div className="space-y-2">
              {PORTFOLIO_LAYOUTS.map(option => (
                <Choice
                  key={option.id}
                  label={option.label}
                  hint={option.hint}
                  active={form.layout === option.id}
                  onClick={() => setForm(f => ({ ...f, layout: option.id }))}
                />
              ))}
            </div>
          </div>
          <div>
            <label className={label}>Organización</label>
            <div className="space-y-2">
              {PORTFOLIO_GROUPINGS.map(option => (
                <Choice
                  key={option.id}
                  label={option.label}
                  hint={option.hint}
                  active={form.grouping === option.id}
                  onClick={() => setForm(f => ({ ...f, grouping: option.id }))}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className={label}>Vista previa</label>
          <PortfolioPreview config={previewConfig} title={form.title} subtitle={form.subtitle} />
        </div>
      </div>

      <div className="b2b-panel p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold">Imagen de portada</h2>
          {form.coverImage && (
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, coverImage: '' }))}
              className="b2b-btn b2b-btn--ghost b2b-btn--sm"
            >
              <X className="w-4 h-4" /> Quitar
            </button>
          )}
        </div>

        {form.coverImage ? (
          <div className="relative rounded-lg overflow-hidden border border-[var(--b2b-border)]">
            <img src={form.coverImage} alt="Portada" className="w-full h-48 object-cover" />
          </div>
        ) : (
          <div className="w-full h-48 rounded-lg border border-dashed border-[var(--b2b-border)] flex items-center justify-center b2b-muted">
            <ImagePlus className="w-5 h-5 mr-2" /> Sin imagen de portada
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3">
          <label className={cn('b2b-btn b2b-btn--ghost b2b-btn--sm cursor-pointer')}>
            <ImagePlus className="w-4 h-4" />
            {form.coverImage ? 'Cambiar imagen' : 'Elegir imagen'}
            <input type="file" accept="image/*" onChange={handleImage} className="sr-only" />
          </label>
          <span className="text-xs b2b-muted self-center">JPG, PNG o WebP · máx. 5 MB</span>
        </div>

        {coverOptions.length > 0 && (
          <div className="mt-5">
            <p className="text-xs b2b-muted mb-2">O elige una foto de tus obras:</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {coverOptions.map(photo => {
                const active = form.coverImage === photo.url;
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, coverImage: photo.url }))}
                    className={cn(
                      'relative h-16 w-24 shrink-0 rounded-lg overflow-hidden border',
                      active
                        ? 'border-[color:var(--b2b-primary)] ring-2 ring-[color:var(--b2b-primary)]/40'
                        : 'border-[var(--b2b-border)] hover:border-[color:var(--b2b-primary)]'
                    )}
                    aria-label={photo.caption || 'Usar como portada'}
                  >
                    <img src={photo.thumbnailUrl || photo.url} alt="" className="w-full h-full object-cover" />
                    {active && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                        <Check className="w-5 h-5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="b2b-panel p-6">
        <h2 className="text-lg font-semibold mb-4">Contacto</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={label}>Teléfono</label>
            <input className={input} value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
          </div>
          <div>
            <label className={label}>Email</label>
            <input type="email" className={input} value={form.contactEmail} onChange={e => setForm({ ...form, contactEmail: e.target.value })} />
          </div>
          <div>
            <label className={label}>WhatsApp</label>
            <input className={input} value={form.contactWhatsapp} onChange={e => setForm({ ...form, contactWhatsapp: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="b2b-panel">
        <div className="b2b-panel__head">
          <h2 className="text-lg font-semibold">Obras destacadas</h2>
          <span className="text-sm b2b-muted">{form.featuredProjectIds.length} seleccionadas</span>
        </div>
        {projects.length === 0 ? (
          <div className="p-8 text-center b2b-muted">No hay proyectos todavía.</div>
        ) : (
          projects.map(project => {
            const count = photos.filter(p => p.projectId === project.id).length;
            const checked = form.featuredProjectIds.includes(project.id);
            return (
              <label key={project.id} className="b2b-panel__row cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded accent-[color:var(--b2b-accent)]"
                  checked={checked}
                  onChange={() => toggleProject(project.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{project.name}</span>
                  <span className="block text-xs b2b-muted">{project.address || 'Sin dirección'}</span>
                </span>
                <span className="b2b-pill">{count} foto{count === 1 ? '' : 's'}</span>
              </label>
            );
          })
        )}
        {form.featuredProjectIds.length === 0 && photos.length > 0 && (
          <div className="px-6 pb-4 text-xs b2b-muted flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5" /> Si no destacas ninguna, se muestran las obras que ya tienen fotos.
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="b2b-btn b2b-btn--primary">
          <Save className="w-4 h-4" /> Guardar portfolio
        </button>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="b2b-btn b2b-btn--ghost">
          <ExternalLink className="w-4 h-4" /> Ver portfolio
        </a>
        {saved && <span className="text-sm text-[color:var(--b2b-success)]">Guardado ✓</span>}
      </div>
    </div>
  );
}

function Choice({
  label,
  hint,
  fontFamily,
  active,
  onClick,
}: {
  label: string;
  hint?: string;
  fontFamily?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-lg border px-3 py-2 transition',
        active
          ? 'border-[color:var(--b2b-primary)] bg-[color:var(--b2b-primary)]/5'
          : 'border-[var(--b2b-border)] hover:border-[color:var(--b2b-primary)]'
      )}
    >
      <span className="block text-sm font-medium" style={fontFamily ? { fontFamily } : undefined}>
        {label}
      </span>
      {hint && <span className="block text-xs b2b-muted">{hint}</span>}
    </button>
  );
}

function PortfolioPreview({
  config,
  title,
  subtitle,
}: {
  config: PortfolioConfig;
  title: string;
  subtitle: string;
}) {
  const { vars, layout, grouping } = resolvePortfolioTheme(config);
  return (
    <div className="rounded-xl border border-[var(--b2b-border)] overflow-hidden">
      <div
        style={{ ...(vars as CSSProperties), background: 'var(--pf-bg)', color: 'var(--pf-text)', fontFamily: 'var(--pf-body-font)' }}
        className="p-4 sm:p-5"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[var(--pf-accent)] text-[var(--pf-accent-contrast)] flex items-center justify-center font-bold shrink-0">
            {(title || 'R').charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="pf-heading text-lg font-bold truncate">{title || 'Tu empresa'}</p>
            <p className="pf-muted text-xs truncate">{subtitle || 'Nuestros trabajos de reforma'}</p>
          </div>
        </div>

        {grouping !== 'none' && (
          <div className="flex items-center gap-2 mt-4 mb-1">
            <span className="pf-heading text-sm font-bold">{previewGroupLabel(grouping)}</span>
            <span className="pf-divider border-t flex-1" />
          </div>
        )}

        <PreviewWorks layout={layout} />

        <div className="pf-surface mt-4 p-4">
          <p className="pf-heading font-semibold">¿Hablamos de tu reforma?</p>
          <button type="button" className="pf-btn mt-3 w-full justify-center">Contactar</button>
        </div>
      </div>
    </div>
  );
}

function previewGroupLabel(grouping: PortfolioGrouping): string {
  switch (grouping) {
    case 'year':
      return '2024';
    case 'location':
      return 'Madrid';
    case 'type':
      return 'Baño';
    case 'status':
      return 'En curso';
    case 'client':
      return 'Cliente';
    default:
      return '';
  }
}

function PreviewCard({ heightClass = 'h-16', title }: { heightClass?: string; title: string }) {
  return (
    <div className="pf-card">
      <div className={cn('pf-soft', heightClass)} />
      <div className="p-3">
        <p className="pf-heading text-sm font-semibold leading-tight">{title}</p>
        <p className="pf-muted text-xs mt-1">Chamberí, Madrid</p>
      </div>
    </div>
  );
}

function PreviewWorks({ layout }: { layout: PortfolioLayout }) {
  if (layout === 'list') {
    return (
      <div className="space-y-2 mt-3">
        {['Reforma de baño', 'Reforma de cocina'].map(t => (
          <div key={t} className="pf-row">
            <div className="pf-soft w-24 shrink-0" />
            <div className="p-3">
              <p className="pf-heading text-sm font-semibold">{t}</p>
              <p className="pf-muted text-xs mt-1">Chamberí, Madrid</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (layout === 'featured') {
    return (
      <div className="space-y-2 mt-4">
        <div className="pf-card">
          <div className="pf-hero-fallback h-20" />
          <div className="p-3">
            <p className="pf-heading text-base font-bold">Reforma destacada</p>
            <p className="pf-muted text-xs mt-1">Chamberí, Madrid</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <PreviewCard title="Reforma de baño" />
          <PreviewCard title="Reforma de cocina" />
        </div>
      </div>
    );
  }

  if (layout === 'masonry') {
    return (
      <div className="columns-2 gap-2 mt-4">
        <div className="mb-2 break-inside-avoid">
          <PreviewCard heightClass="h-24" title="Reforma integral" />
        </div>
        <div className="mb-2 break-inside-avoid">
          <PreviewCard heightClass="h-12" title="Pintura" />
        </div>
        <div className="mb-2 break-inside-avoid">
          <PreviewCard heightClass="h-16" title="Reforma de baño" />
        </div>
        <div className="mb-2 break-inside-avoid">
          <PreviewCard heightClass="h-20" title="Reforma de cocina" />
        </div>
      </div>
    );
  }

  if (layout === 'showcase') {
    return (
      <div className="mt-4 space-y-2">
        <p className="pf-heading text-sm font-bold">Reforma integral</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="pf-soft h-14 rounded-[var(--pf-radius)]" />
          <div className="pf-soft h-14 rounded-[var(--pf-radius)]" />
          <div className="pf-soft h-14 rounded-[var(--pf-radius)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 mt-4">
      <PreviewCard title="Reforma de baño" />
      <PreviewCard title="Reforma de cocina" />
      <PreviewCard title="Reforma integral" />
      <PreviewCard title="Local comercial" />
    </div>
  );
}
